/**
 * Faculty / honorary advisors — single source of truth for names, affiliations, photos, links.
 *
 * How keys work
 * ------------
 * 1. `ADVISORS` is a map: each **key** (e.g. `MCCLURE`, `GLOS_HO`) is a string constant naming one person.
 *    The **value** is that person’s `{ name, affiliation, photoUrl?, link? }`.
 *
 * 2. **Page lists** are arrays of those same key strings, in **display order** (by rank, then surname):
 *    Emeritus → Full Professor → Associate → Assistant → Lecturer → other roles (e.g. directors).
 *    - `CORTEX_FACULTY_ADVISOR_KEYS` — CORTEX faculty grid (home + /cortex), rank order.
 *    - `JELTR_HONORARY_ADVISOR_KEYS` — JELTR “Honorary Advisors” section.
 *
 * 3. `getAdvisorsByKeys(['MATTHEWS', 'MCCLURE'], 'blue')` walks the array in order, looks up each key
 *    in `ADVISORS`, and returns card props (with `theme` for `FacultyAdvisorCard`). Unknown keys are skipped.
 *
 * To add someone: (a) add an entry under `ADVISORS`, (b) append their key to every list where they should appear.
 */

export const ADVISORS = {
  MATTHEWS: {
    id: 'matthews',
    name: 'Prof. Stephen MATTHEWS',
    affiliation: 'Emeritus Professor, School of Humanities (Linguistics), The University of Hong Kong',
    photoUrl: '/asset/images/profiles/2526/Matthews.jpg',
    link: 'https://hub.hku.hk/cris/rp/rp01207',
  },
  MCCLURE: {
    id: 'mcclure',
    name: 'Prof. Alastair MCCLURE',
    affiliation: 'Assistant Professor, School of Humanities (History), The University of Hong Kong',
    photoUrl: '/asset/images/profiles/2526/McClure.webp',
    link: 'https://history.hku.hk/staff-a-mcclure/',
  },
  ROELLINGHOFF: {
    id: 'roellinghoff',
    name: 'Prof. Michael ROELLINGHOFF',
    affiliation: 'Assistant Professor, School of Modern Languages and Cultures (Japanese Studies), The University of Hong Kong',
    photoUrl: '/asset/images/profiles/2526/Roellinghoff.jpg',
    link: 'https://japanese.hku.hk/staff/RMichael.html',
  },
  TSANG: {
    id: 'tsang',
    name: 'Dr. Carol TSANG',
    affiliation: 'Lecturer, School of Humanities (History), The University of Hong Kong',
    photoUrl: '/asset/images/profiles/2526/Tsang.webp',
    link: 'https://history.hku.hk/staff-c-tsang/',
  },
  GABRIEL_TSANG: {
    id: 'gabriel-tsang',
    name: 'Prof. Gabriel TSANG',
    affiliation: 'Assistant Professor, Department of Chinese Language and Literature, Hong Kong Baptist University',
    photoUrl: '/asset/images/profiles/2526/Zeng.jpeg',
    link: 'https://chi.hkbu.edu.hk/personnel/teaching-staff/zengfanyu.html',
  },
  YUEN: {
    id: 'man-leuk-yuen',
    name: 'Dr. Man Leuk YUEN',
    affiliation: 'STEAM Director, Eureka Language Service',
    photoUrl: '/asset/images/profiles/2526/Yuen.jpg',
    link: 'https://p-articles.com/contributors/%E9%98%AE%E6%96%87%E7%95%A5',
  },
  TONG: {
    id: 'tong-yui',
    name: 'Prof. Yui TONG',
    affiliation: 'Associate Professor, Department of Humanities and Creative Writing, Hong Kong Baptist University',
    photoUrl: '/asset/images/profiles/2526/Tong.webp',
    link: 'https://hmw.hkbu.edu.hk/people/dr-tong-yui/',
  },
  GLOS_HO: {
    id: 'glos-ho',
    name: 'Dr. Glos HO',
    affiliation:
      'Director of Transdisciplinary Undergraduate Programmes, Hong Kong Baptist University',
    photoUrl: '/asset/images/profiles/2526/GlosHo.jpg',
    link: 'https://scholars.hkbu.edu.hk/en/persons/GLOSHO/',
  },
  CHEUNG_SING_HANG: {
    id: 'cheung-sing-hang',
    name: 'Prof. Sing-Hang CHEUNG',
    affiliation: 'Associate Professor, Department of Psychology, The University of Hong Kong',
    photoUrl: '/asset/images/profiles/2526/CheungSingHang.jpg',
    link: 'https://psychology.hku.hk/people/cheung-sing-hang/',
  },
  BECKER: {
    id: 'benjamin-becker',
    name: 'Prof. Benjamin BECKER',
    affiliation: 'Professor, Department of Psychology, The University of Hong Kong',
    photoUrl: '/asset/images/profiles/2526/Becker.jpg',
    link: 'https://psychology.hku.hk/people/bbecker/',
  },
  CROLLA: {
    id: 'kristof-crolla',
    name: 'Prof. Kristof CROLLA',
    affiliation: 'Associate Professor, Department of Architecture, The University of Hong Kong',
    photoUrl: '/asset/images/profiles/2526/Crolla.jpg',
    link: 'https://www.arch.hku.hk/staff/arch/crolla-kristof/',
  },
  ELAM: {
    id: 'daniel-elam',
    name: 'Prof. Daniel ELAM',
    affiliation: 'Assistant Professor, Department of Comparative Literature, The University of Hong Kong',
    photoUrl: '/asset/images/profiles/2526/Elam.jpeg',
    /** Primary card target: CORTEX 2026 keynote page (faculty profile is linked from that page only). */
    link: '/cortex/keynote-speech-2026',
    profileLink: 'https://complit.hku.hk/index.php/faculty/daniel-elam/',
  },
};

/**
 * CORTEX – Faculty advisors in the grid (rank order).
 */
export const CORTEX_FACULTY_ADVISOR_KEYS = [
  'MATTHEWS', // Emeritus
  'BECKER', // Full Professor
  'CHEUNG_SING_HANG',
  'CROLLA',
  'TONG', // Associate
  'ELAM',
  'GABRIEL_TSANG',
  'MCCLURE', // Assistant
  'ROELLINGHOFF',
  'TSANG', // Lecturer
  'GLOS_HO',
  'YUEN', // Director / other
];

/** JELTR – Honorary Advisors (all panels; rank order). Prof. Matthews is CORTEX-only. */
export const JELTR_HONORARY_ADVISOR_KEYS = [
  'BECKER', // Full Professor
  'CHEUNG_SING_HANG',
  'CROLLA',
  'TONG', // Associate
  'ELAM',
  'GABRIEL_TSANG',
  'MCCLURE',
  'ROELLINGHOFF', // Assistant
  'TSANG', // Lecturer
  'GLOS_HO',
  'YUEN', // Director / other
];

/**
 * Map advisor keys to props for FacultyAdvisorCard.
 * @param {string[]} keys - Advisor keys in display order
 * @param {string} [theme='blue'] - Card theme (blue, purple, orange)
 * @returns {Array<{ name: string, affiliation: string, photoUrl?: string, link?: string, profileLink?: string, theme: string }>}
 */
export const getAdvisorsByKeys = (keys, theme = 'blue') =>
  keys
    .map((key) => {
      const advisor = ADVISORS[key];
      if (!advisor) return null;
      return {
        name: advisor.name,
        affiliation: advisor.affiliation,
        ...(advisor.photoUrl && { photoUrl: advisor.photoUrl }),
        ...(advisor.link && { link: advisor.link }),
        ...(advisor.profileLink && { profileLink: advisor.profileLink }),
        theme,
      };
    })
    .filter(Boolean);
